import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
    ArrowLeft,
    Edit,
    Calendar,
    MapPin,
    Briefcase,
    Users,
    Globe,
    Clock,
    CheckCircle,
    AlertCircle,
    Building,
    FileText,
    Loader2,
    Copy
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate';
import jobService from '../../../api/services/jobService';
import { formatDate } from '../../../utils/stringUtils';
import toast, { Toaster } from 'react-hot-toast';

const ViewJob = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { data: job, isLoading, error } = useQuery({
        queryKey: ['job', id],
        queryFn: async () => {
            const result = await jobService.getJobById(id);
            if (result.success) return result.data;
            throw new Error(result.error || 'Failed to fetch job details');
        },
        enabled: !!id
    });

    const handleCopyJobLink = () => {
        const jobLink = `${window.location.origin}/job-application/${job.jobId || job._id}`;
        navigator.clipboard.writeText(jobLink);
        toast.success("Job link copied!");
    };

    const KpiMiniCard = ({ icon: Icon, title, value, color = 'bg-brand' }) => (
        <div className="relative overflow-hidden bg-white rounded-2xl p-4 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
            <div className={`absolute -right-8 -top-8 h-28 w-28 rounded-full ${color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
            <div className="relative z-10 flex items-center gap-4">
                <div className={`p-2.5 rounded-xl ${color} shadow-md`}>
                    <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{title}</p>
                    <p className="text-sm font-bold text-gray-900 tracking-tight break-words">{value}</p>
                </div>
            </div>
        </div>
    );

    // Mapping for user-friendly website region names
    const websiteRegionDisplay = {
        MOBILOITTE: 'Mobiloitte',
        MOBILOITTE_UAE: 'Mobiloitte UAE',
        MOBILOITTE_USA: 'Mobiloitte USA',
        MOBILOITTE_UK: 'Mobiloitte UK',
        MOBILOITTE_SOUTH_AFRICA: 'Mobiloitte South Africa',
        MOBILOITTE_SINGAPORE: 'Mobiloitte Singapore',
        MOBILOITTE_GROUP: 'Mobiloitte Group'
    };


    const getStatusColor = (status) => {
        if (!status) return "bg-gray-100 text-gray-800";
        switch (status.toLowerCase()) {
            case "active":
                return "bg-blue-50 text-brand border-blue-100";
            case "draft":
                return "bg-amber-50 text-amber-800 border-amber-100";
            case "closed":
                return "bg-red-50 text-red-800 border-red-100";
            default:
                return "bg-gray-50 text-gray-800 border-gray-100";
        }
    };

    const getPriorityColor = (priority) => {
        if (!priority) return "bg-gray-100 text-gray-800";
        switch (priority.toLowerCase()) {
            case "high":
                return "bg-red-50 text-red-800 border-red-100";
            case "medium":
                return "bg-amber-50 text-amber-800 border-amber-100";
            case "low":
                return "bg-blue-50 text-brand border-blue-100";
            default:
                return "bg-gray-50 text-gray-800 border-gray-100";
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-brand" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <h2 className="text-base font-semibold text-gray-900 mb-2">Error Loading Job</h2>
                <p className="text-gray-600 mb-4">{error.message}</p>
                <Button onClick={() => navigate(location.state?.from || -1)} variant="secondary">
                    Go Back
                </Button>
            </div>
        );
    }

    if (!job) return null;

    return (
        <div className="space-y-6 w-full">
            <Toaster position="top-right" />

            {/* Navigation & Actions */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className='flex items-center gap-4'>
                    <button
                        onClick={() => navigate(location.state?.from || -1)}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-blue-50/30 transition-colors"
                        aria-label="Go back"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900">Job Details</h1>
                        <p className='text-xs text-gray-600 mt-1'>View job details and manage applications</p>
                    </div>
                </div>
                <div className='flex flex-wrap items-center gap-3'>
                    <Button
                        variant="outline"
                        onClick={handleCopyJobLink}
                        className="flex items-center border-brand text-brand hover:bg-blue-50/30"
                    >
                        <Copy className="w-5 h-5 mr-2" />
                        Copy Link
                    </Button>
                    <PermissionGate permission="recruitment" action="update">
                        <Button
                            variant="primary"
                            onClick={() => navigate(`/edit-job/${id}`, { state: { from: location.state?.from } })}
                            className="flex items-center bg-brand text-white hover:bg-brand-dark"
                        >
                            <Edit className="w-5 h-5 mr-2" />
                            Edit Job Post
                        </Button>
                    </PermissionGate>
                </div>
            </div>

            {/* Hero Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="flex-1">
                        <h1 className="text-lg font-bold text-gray-900 mb-3 capitalize break-all">
                            {job.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4 text-gray-600">
                            <span className="flex items-center bg-blue-50/30 text-brand px-3 py-1 rounded-full text-xs font-semibold border border-blue-100 break-words max-w-full">
                                <Building className="h-4 w-4 mr-2 flex-shrink-0" />
                                {job.department}
                            </span>
                            <span className="flex items-center bg-blue-50/20 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 break-words max-w-full">
                                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                                {job.location}
                            </span>
                            <span className="flex items-center bg-blue-50/20 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 break-words max-w-full">
                                <Briefcase className="h-4 w-4 mr-2 flex-shrink-0" />
                                {job.type === 'full_time' ? 'Full-time' : job.type === 'part_time' ? 'Part-time' : job.type}
                            </span>
                            <span className="flex items-center bg-blue-50/20 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 break-words max-w-full capitalize">
                                <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                                Gender: {job.preferredGender || 'Any'}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <div className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center justify-center border ${getStatusColor(job.status)}`}>
                            <div className="w-2 h-2 rounded-full bg-current mr-2" />
                            {job.status.toUpperCase()}
                        </div>
                        <div className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center justify-center border ${getPriorityColor(job.priority)}`}>
                            {job.priority.toUpperCase()} PRIORITY
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KpiMiniCard
                    icon={Users}
                    title="Applications"
                    value={`${job.applicationsCount || 0} received`}
                    color="bg-brand"
                />
                <KpiMiniCard
                    icon={Briefcase}
                    title="Experience"
                    value={job.experience?.name || (job.experience ? (job.experience.min === 10 && (!job.experience.max || job.experience.max === 0) ? '10+ years' : `${job.experience.min}-${job.experience.max} years`) : 'Not specified')}
                    color="bg-indigo-600"
                />
                <KpiMiniCard
                    icon={Calendar}
                    title="Posted"
                    value={job.postedDate ? formatDate(job.postedDate, { includeTime: false }) : 'Recently'}
                    color="bg-cyan-600"
                />
                <KpiMiniCard
                    icon={Clock}
                    title="Deadline"
                    value={job.deadline ? formatDate(job.deadline, { includeTime: false }) : 'Ongoing'}
                    color="bg-rose-600"
                />
                <KpiMiniCard
                    icon={Globe}
                    title="Website Region"
                    value={job.websiteRegion?.map(r => websiteRegionDisplay[r] || r).join(', ') || 'N/A'}
                    color="bg-green-600"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Description */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                            <FileText className="h-6 w-6 mr-3 text-purple-600" />
                            Job Description
                        </h2>
                        <div className="prose prose-gray max-w-none prose-headings:font-bold prose-p:text-gray-700">
                            <div
                                className="text-sm text-gray-700 leading-relaxed break-words 
                                          [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:text-gray-900
                                          [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:text-gray-900
                                          [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:text-gray-900
                                          [&_h4]:text-base [&_h4]:font-bold [&_h4]:mb-2 [&_h4]:text-gray-900
                                          [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-4 [&_ul]:mt-2
                                          [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-4 [&_ol]:mt-2
                                          [&_li]:mb-1 [&_li]:pl-1
                                          [&_p]:mb-4 [&_p]:leading-relaxed
                                          [&_strong]:font-bold [&_strong]:text-gray-900
                                          [&_em]:italic
                                          [&_u]:underline
                                          [&_blockquote]:border-l-4 [&_blockquote]:border-gray-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4
                                          [&_a]:text-brand [&_a]:underline [&_a]:hover:text-brand-dark
                                          [&_pre]:bg-gray-50 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-4
                                          [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description || 'No description available') }}
                            />
                        </div>
                    </section>

                    {/* Requirements */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                            <AlertCircle className="h-6 w-6 mr-3 text-[#0F2942]" />
                            Requirements
                        </h2>
                        {job.requirements && job.requirements.length > 0 ? (
                            <ul className="space-y-3">
                                {job.requirements.map((req, index) => (
                                    <li key={index} className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-[#0F2942] mr-3 mt-1 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 leading-relaxed break-words capitalize">{req}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500 italic">No specific requirements mentioned.</p>
                        )}
                    </section>

                    {/* Skills */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                            <Users className="h-6 w-6 mr-3 text-brand" />
                            Skills Required
                        </h2>
                        {job.skills && job.skills.length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                                {job.skills.map((skill, index) => (
                                    <span key={index} className="bg-blue-50/30 text-brand px-3 py-1.5 rounded-xl text-xs font-semibold border border-blue-100 break-words max-w-full capitalize">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">No specific skills mentioned.</p>
                        )}
                    </section>
                </div>

                {/* Sidebar Info */}
                <div className="lg:col-span-1 space-y-8">
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-base font-bold text-gray-900 mb-5 border-b pb-3">Job Overview</h3>
                        <div className="space-y-6">
                            <div className="flex items-start">
                                <div className="bg-blue-50/30 p-2 rounded-lg mr-4 border border-blue-100">
                                    <Briefcase className="h-5 w-5 text-brand" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Experience</p>
                                    <p className="font-semibold text-gray-900">
                                        {job.experience?.name || (job.experience ? (job.experience.min === 10 && (!job.experience.max || job.experience.max === 0) ? '10+ years' : `${job.experience.min}-${job.experience.max} years`) : 'Not specified')}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="bg-blue-50/20 p-2 rounded-lg mr-4 border border-gray-200">
                                    <Users className="h-5 w-5 text-gray-700" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Preferred Gender</p>
                                    <p className="font-semibold text-gray-900 capitalize">
                                        {job.preferredGender || 'Any'}
                                    </p>
                                </div>
                            </div>


                            <div className="flex items-start">
                                <div className="bg-blue-50/30 p-2 rounded-lg mr-4 border border-blue-100">
                                    <span className="text-brand font-bold text-lg">₹</span>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Offered Salary</p>
                                    <p className="font-semibold text-gray-900">
                                        {job.salary ? `₹${job.salary.min.toLocaleString()} - ₹${job.salary.max.toLocaleString()}` : 'Competitive'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="bg-blue-50/20 p-2 rounded-lg mr-4 border border-gray-200">
                                    <Calendar className="h-5 w-5 text-gray-700" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Date Posted</p>
                                    <p className="font-semibold text-gray-900">
                                        {job.postedDate ? formatDate(job.postedDate, { includeTime: false }) : 'Recently'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="bg-red-50 p-2 rounded-lg mr-4">
                                    <Clock className="h-5 w-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Deadline</p>
                                    <p className="font-semibold text-gray-900">
                                        {job.deadline ? formatDate(job.deadline, { includeTime: false }) : 'Ongoing'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="bg-blue-50/20 p-2 rounded-lg mr-4 border border-gray-200">
                                    <Users className="h-5 w-5 text-gray-700" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Applications</p>
                                    <p className="font-semibold text-gray-900">{job.applicationsCount || 0} received</p>
                                </div>
                            </div>
                        </div>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default ViewJob;
